import React, { useMemo, useState, useCallback } from "react";
import {
  Alert,
  Select,
  Text,
  TextInput,
  NumberInput,
  Loader,
  Group,
  Stack,
  Paper,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  buildApprovedFundOptions,
  findApprovedFundOptionKey,
  mapApprovalFundToArProduct,
} from "../helpers/orderArFormFundUtils";
import {
  AR_FORM_NUMBER_INPUT_PROPS,
  EMPTY_AR_FORM_PRODUCT,
  RPQ_RISK_LABELS,
} from "../helpers/arFormUtils";
import { useApprovedFundList } from "../contexts/approvedFundListContext";
import { attachSourceIndexes } from "../helpers/approvalFundListFundFields";
import {
  buildTableFilterChange,
  filterFunds,
} from "../helpers/approvalFundListFundsUtils";
import OrderArFormFundFilters from "./orderArFormFundFilters";
import {
  canClientBuyDerivativesFund,
  filterFundsByDerivativesEligibility,
  getDerivativesFundPurchaseError,
} from "../helpers/derivativesFundEligibility";
import {
  filterFundsByClientRpq,
  getFundMissingRiskRatingWarning,
  getRpqFundListWarning,
  getRpqFundPurchaseError,
  parseFundRiskRating,
} from "../helpers/rpqFundEligibility";

const OrderArFormApprovedFundSelect = ({
  form,
  label = "Fund Name 基金名稱",
  eligibilityClient,
  rpqContext = { riskLevel: null, status: "NONE", restrictFunds: false },
}) => {
  const { funds, fundListName, loading, error } = useApprovedFundList();
  const [tableFilterQuery, setTableFilterQuery] = useState({});

  const indexedFunds = useMemo(() => attachSourceIndexes(funds), [funds]);
  const rpqEligibleFunds = useMemo(
    () => filterFundsByClientRpq(indexedFunds, rpqContext),
    [indexedFunds, rpqContext]
  );
  const eligibleFunds = useMemo(
    () =>
      eligibilityClient
        ? filterFundsByDerivativesEligibility(
            rpqEligibleFunds,
            eligibilityClient
          )
        : rpqEligibleFunds,
    [rpqEligibleFunds, eligibilityClient]
  );
  const filteredFunds = useMemo(
    () => filterFunds(eligibleFunds, { searchText: "", tableFilterQuery }),
    [eligibleFunds, tableFilterQuery]
  );
  const hideDerivativesFunds =
    Boolean(eligibilityClient) &&
    !canClientBuyDerivativesFund(eligibilityClient) &&
    rpqEligibleFunds.length !== eligibleFunds.length;

  const selectedKey = findApprovedFundOptionKey(form.values, funds);
  const rpqListWarning = getRpqFundListWarning(rpqContext);
  const selectedMissingRatingWarning = getFundMissingRiskRatingWarning(
    form.values
  );

  const fundsForSelect = useMemo(() => {
    if (selectedKey == null || selectedKey === "") return filteredFunds;

    const selectedIndex = Number(selectedKey);
    if (Number.isNaN(selectedIndex)) return filteredFunds;

    const alreadyIncluded = filteredFunds.some(
      (fund) => fund.__sourceIndex === selectedIndex
    );
    if (alreadyIncluded) return filteredFunds;

    const selectedFund = indexedFunds.find(
      (fund) => fund.__sourceIndex === selectedIndex
    );
    return selectedFund ? [selectedFund, ...filteredFunds] : filteredFunds;
  }, [filteredFunds, indexedFunds, selectedKey]);

  const options = useMemo(
    () =>
      buildApprovedFundOptions(fundsForSelect).map((option) => {
        const fund = fundsForSelect.find(
          (item) => String(item.__sourceIndex ?? "") === option.value
        );
        if (parseFundRiskRating(fund) != null) return option;
        return { ...option, label: `${option.label} · Risk rating missing` };
      }),
    [fundsForSelect]
  );

  const onTableFilterChange = useMemo(
    () =>
      buildTableFilterChange({
        setTableFilterQuery,
        setCurrentPage: () => {},
      }),
    []
  );

  const handleFilterChange = useCallback(
    (field, values, possibleValues) => {
      if (field == null) {
        setTableFilterQuery({});
        return;
      }
      onTableFilterChange(field, values, possibleValues);
    },
    [onTableFilterChange]
  );

  const applyFundSelection = (key) => {
    form.markUserEdited?.();
    if (key == null || key === "") {
      form.setValues({
        ...form.values,
        ...EMPTY_AR_FORM_PRODUCT(),
        _approvedFundKey: null,
      });
      return;
    }

    const fund = funds[Number(key)];
    const mapped = mapApprovalFundToArProduct(fund);
    if (eligibilityClient) {
      const restrictionError = getDerivativesFundPurchaseError(
        eligibilityClient,
        mapped
      );
      if (restrictionError) {
        showNotification({
          color: "red",
          title: "Derivative fund not allowed",
          message: restrictionError,
        });
        return;
      }
    }

    const rpqError = getRpqFundPurchaseError(rpqContext, mapped);
    if (rpqError) {
      showNotification({
        color: "red",
        title: "Fund not allowed for client RPQ",
        message: rpqError,
      });
      return;
    }

    const missingRatingWarning = getFundMissingRiskRatingWarning(mapped);
    if (missingRatingWarning) {
      showNotification({
        color: "yellow",
        title: "Fund risk rating missing",
        message: missingRatingWarning,
      });
    }

    form.setValues({
      ...form.values,
      ...mapped,
      _approvedFundKey: key,
    });
  };

  if (loading) {
    return (
      <Group spacing="xs" mb="xs">
        <Loader size="xs" />
        <Text size="xs" color="dimmed">
          Loading approved fund list…
        </Text>
      </Group>
    );
  }

  if (!funds.length) {
    return (
      <Stack spacing="sm">
        {rpqListWarning && (
          <Alert color="yellow" variant="light">
            {rpqListWarning}
          </Alert>
        )}
        <Text size="xs" color="orange">
          {error
            ? error
            : fundListName
            ? `${fundListName} has no funds. Enter fund details manually.`
            : "No effective approved fund list is available. Enter fund details manually."}
        </Text>
        <TextInput
          label={label}
          required
          {...form.getInputProps("fundName")}
        />
        <NumberInput
          label="Risk Rating (1–5) 風險評級"
          min={1}
          max={5}
          precision={0}
          {...AR_FORM_NUMBER_INPUT_PROPS}
          {...form.getInputProps("riskRating")}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing="lg">
      {fundListName && (
        <Text size="sm" color="dimmed">
          {fundListName}
        </Text>
      )}

      {rpqListWarning ? (
        <Alert color="yellow" variant="light" title="RPQ 風險取向">
          {rpqListWarning}
        </Alert>
      ) : rpqContext.restrictFunds ? (
        <Alert color="blue" variant="light" title="RPQ fund filter 風險取向篩選">
          Only funds with risk rating ≤ {rpqContext.riskLevel}
          {RPQ_RISK_LABELS[rpqContext.riskLevel]
            ? ` (${RPQ_RISK_LABELS[rpqContext.riskLevel]})`
            : ""}{" "}
          are listed, matching the latest RPQ level.
          <br />
          只顯示風險評級 ≤ {rpqContext.riskLevel} 的基金，以配合最新 RPQ 風險級別。
          <br />
          Funds without a risk rating are still shown. 沒有風險評級的基金仍會顯示。
          {indexedFunds.length !== rpqEligibleFunds.length
            ? ` ${rpqEligibleFunds.length} of ${indexedFunds.length} approved funds match.`
            : ""}
        </Alert>
      ) : null}

      {hideDerivativesFunds && (
        <Alert
          color="blue"
          variant="light"
          title="Derivatives Fund 衍生工具基金"
        >
          Derivative funds are hidden. Only Professional Investors or clients
          with signed Knowledge of Derivatives can select them.
          <br />
          已隱藏衍生工具基金。僅專業投資者或已簽署衍生工具知識聲明的客戶可選擇。
        </Alert>
      )}

      <Paper withBorder p="md" radius="sm">
        <OrderArFormFundFilters
          funds={eligibleFunds}
          tableFilterQuery={tableFilterQuery}
          onFilterChange={handleFilterChange}
          filteredCount={filteredFunds.length}
          totalCount={eligibleFunds.length}
          listTotalCount={indexedFunds.length}
        />
      </Paper>

      {rpqContext.restrictFunds && rpqEligibleFunds.length === 0 && (
        <Text size="xs" color="orange">
          No funds in the approved list match the latest RPQ level.
        </Text>
      )}

      <Select
        label={label}
        placeholder="Search and select a fund"
        required
        searchable
        clearable
        nothingFound="No matching fund"
        data={options}
        value={selectedKey}
        onChange={applyFundSelection}
        size="md"
      />

      {selectedMissingRatingWarning && form.values?.fundName && (
        <Alert color="yellow" variant="light">
          {selectedMissingRatingWarning}
        </Alert>
      )}
    </Stack>
  );
};

export default OrderArFormApprovedFundSelect;
